import { Injectable, signal } from '@angular/core';

const RECENT_TTL_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class RecentlyCreatedRegistry {
  private readonly ids = signal<ReadonlySet<string>>(new Set());
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  /** Flags one or more entities as just created by the current user. */
  mark(...ids: readonly (string | null | undefined)[]): void {
    const newIds = ids.filter((id): id is string => !!id);
    if (newIds.length === 0) {
      return;
    }

    this.ids.update(current => new Set([...current, ...newIds]));

    for (const id of newIds) {
      const pending = this.timers.get(id);
      if (pending !== undefined) {
        clearTimeout(pending);
      }
      this.timers.set(
        id,
        setTimeout(() => this.clear(id), RECENT_TTL_MS)
      );
    }
  }

  /** Reactive check consumed by `HighlightInsertRowDirective`. */
  isRecent(id: string | null | undefined): boolean {
    return !!id && this.ids().has(id);
  }

  /** Drops the flag, so the highlight is not replayed when the row is re-rendered. */
  clear(id: string): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    this.ids.update(current => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }
}
